import { useNavigate } from 'react-router-dom'

import { Box, Button, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import WorkSpaceItem from 'modules/locations/components/WorkSpaces/WorkSpaceItem'
import { useMapWorkAreasFlag } from 'modules/locations/hooks/useMapWorkAreasFlag'
import { WorkAreaNodes } from 'modules/locations/hooks/useWorkArea'
import { WorkAreaMapNodes } from 'modules/space_control/hooks/useWorkAreaMaps'
import { WorkSpace } from 'modules/space_control/hooks/useWorkSpace'

import Options from './Options'

interface PropsWorkArea {
  locationId: number
  workArea: NonNullable<WorkAreaNodes>
  workSpaces: WorkSpace[]
  workAreaMap?: WorkAreaMapNodes
}

const WorkAreaItem = ({ locationId, workArea, workSpaces, workAreaMap }: PropsWorkArea) => {
  const { navigation } = useNavigation()
  const { moduleAvailability } = useModuleAvailability(AvailableModules.space_control)
  const spacesEnabled = moduleAvailability.available
  const canAddSpace = users.current.policy.can('spaceControl.workSpaceCreate')
  const navigate = useNavigate()
  const disableArchiveButton = workSpaces.length > 0
  const [isMapTooltipOpen, toggleMapTooltip] = useToggle(false)
  const hasMapWorkAreasFlag = useMapWorkAreasFlag()
  const canManageSpaces = spacesEnabled && canAddSpace && hasMapWorkAreasFlag

  const addSpaceClick = () => {
    track(trackingEvents.spaceControl.addSpace, {})
    navigate(
      navigation.settings.locations.locationsId.workAreas.addWorkSpace.fullPath({
        id: locationId,
        workAreaId: workArea.id,
      })
    )
  }

  const canManageWorkArea = users.current.policy.can('locations.workAreaManage')
  const WorkAreaData = () => (
    <>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Box gap="s12" flexDirection="row" alignItems="center">
          <>
            <Text.Normal size="300" weight="medium" color="grey1000">
              {workArea.name}
            </Text.Normal>
            {spacesEnabled && (
              <Text.Normal size="200" weight="normal" color="grey800">
                {i18n.tp('settings.locations.location.work_areas.spaces_count', {
                  count: workSpaces.length,
                })}
              </Text.Normal>
            )}
          </>
        </Box>

        <Box flexDirection="row" alignItems="center" justifyContent="spaceAround" gap="s8">
          {spacesEnabled && canAddSpace && !hasMapWorkAreasFlag && (
            <Button type="secondary" onClick={addSpaceClick}>
              {i18n.t('settings.locations.location.work_areas.add_space')}
            </Button>
          )}
          {hasMapWorkAreasFlag && (
            <Box onMouseEnter={toggleMapTooltip(true)} onMouseLeave={toggleMapTooltip(false)}>
              <Tooltip.Alt
                attachment="top center"
                open={isMapTooltipOpen}
                label={
                  workAreaMap?.image?.url
                    ? i18n.t('settings.locations.location.work_areas.map.has_map')
                    : i18n.t('settings.locations.location.work_areas.map.no_map')
                }
              >
                <Button
                  type="secondary"
                  disabled={!workAreaMap?.image?.url}
                  onClick={() =>
                    workAreaMap?.image?.url && window.open(workAreaMap.image.url, '_blank')
                  }
                  iconName="pin"
                />
              </Tooltip.Alt>
            </Box>
          )}
          {canManageWorkArea && (
            <Options
              workArea={workArea}
              locationId={locationId}
              disableArchiveButton={disableArchiveButton}
              canManageSpaces={canManageSpaces}
            />
          )}
        </Box>
      </Box>
    </>
  )

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
    >
      <Box
        paddingX="s24"
        paddingY="s16"
        borderRadius={{ topLeft: 'abs008', topRight: 'abs008' }}
        {...(workSpaces.length > 0
          ? {
              border: {
                bottom: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              },
            }
          : {})}
        {...(spacesEnabled ? { background: 'grey200' } : {})}
      >
        <WorkAreaData />
      </Box>
      {workSpaces.length > 0 && !hasMapWorkAreasFlag && (
        <>
          {workSpaces.map((workSpace: WorkSpace) => (
            <WorkSpaceItem
              key={`${workSpace.id}${workSpace.name}`}
              workSpace={workSpace}
              locationId={locationId}
              workAreaId={+workArea.id}
            />
          ))}
        </>
      )}
    </Box>
  )
}

export default observer(WorkAreaItem)
